import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useKeepFocusedFieldVisible } from '@/shared/hooks/useKeepFocusedFieldVisible'
import { BackgroundEffectsDialog } from '@vc/views/dialogs/BackgroundEffectsDialog'
import { useMeetingPreferencesStore } from '@/features/meeting/store/meeting-preferences.store'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import {
  DevicePermissionDialog,
  MicPermissionDialog,
  ensureDevicePermission,
  queryMediaPermissionsGranted,
  speakerTestService,
  useDeviceEnumeration,
  useDevicesStore,
  useMediaPreview,
  useMicLevel,
} from '@/features/devices'
import { PreJoinHeader } from '../components/DashboardHeader'
import { DevicePreview } from '../components/DevicePreview'
import { DeviceSelectors } from '../components/DeviceSelectors'
import { GettingReadyPanel } from '../components/GettingReadyPanel'
import { ReadyToJoinPanel } from '../components/ReadyToJoinPanel'
import { usePreJoinFlow } from '../hooks/usePreJoinFlow'
import { useFaceFraming } from '../hooks/useFaceFraming'
import { isPassiveJoinUrl } from '@/features/meetings/utils/joinLinks'
import { config } from '@/config/env'

/**
 * One footer link, or plain text when nothing is configured for it.
 *
 * All four of these shipped as `href="#"`, so clicking Terms of Service or the
 * Privacy Statement did nothing at all — the browser followed the fragment
 * back to the same page. The URLs now come from `config.legal`, the same pair
 * the sign-in screen has always linked.
 *
 * A label with no URL renders as a `<span>` rather than a dead anchor: it
 * still reads as part of the sentence, but it no longer invites a click it
 * cannot answer. `noopener` because these open in a new tab and the opened
 * page has no business reaching back into the meeting.
 */
function LegalLink({ href, children }: { href: string; children: ReactNode }) {
  if (!href) return <span>{children}</span>
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-primary transition-colors hover:text-primary-600"
    >
      {children}
    </a>
  )
}

interface PreJoinPageProps {
  onJoinMeeting: (userName: string, meetingCode: string) => void
  initialUserName?: string
  initialMeetingCode?: string
  onWebinarClick?: () => void
  onWaitingLobbyClick?: () => void
  isWebinar?: boolean
  /** Every seat is taken: the Join button stops loading and stays disabled. */
  joinBlocked?: boolean
}

export function PreJoinPage({
  onJoinMeeting,
  initialUserName = '',
  initialMeetingCode = '',
  onWebinarClick,
  onWaitingLobbyClick,
  isWebinar,
  joinBlocked,
}: PreJoinPageProps) {
  const t = useTranslation()
  useKeepFocusedFieldVisible()

  const flow = usePreJoinFlow({ initialUserName, initialMeetingCode })
  const { mics, cams, speakers } = useDeviceEnumeration()
  // Two streams, one per device. The mic and the camera no longer share a
  // lifetime, so toggling one cannot restart the other.
  const { audioStream, videoStream, error } = useMediaPreview()
  const micLevel = useMicLevel(audioStream)

  // Passive attendees preview nothing: no devices are opened for them, so the
  // camera frame and the mic/camera toggles would be describing hardware that
  // is not running.
  const viewOnly = isPassiveJoinUrl()
  const micEnabled = useDevicesStore((s) => s.micEnabled)
  const camEnabled = useDevicesStore((s) => s.camEnabled)
  const setMicEnabled = useDevicesStore((s) => s.setMicEnabled)
  const setCamEnabled = useDevicesStore((s) => s.setCamEnabled)
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId)

  // Shared with the meeting rather than local: mirroring you chose in the lobby
  // is a preference about how you see yourself, and it used to evaporate the
  // moment you joined.
  const videoFlipped = useMeetingPreferencesStore((s) => s.mirrorOwnVideo)
  const setMirrorOwnVideo = useMeetingPreferencesStore((s) => s.setMirrorOwnVideo)
  const [framingEnabled, setFramingEnabled] = useState(false)
  // Manual override slider stays available (drag the dot on the preview)
  // but is overridden by the auto-framing hook below whenever framing is on.
  const [manualFaceOffset, setManualFaceOffset] = useState({ x: 0, y: 0 })
  const autoFaceOffset = useFaceFraming(videoStream ?? null, framingEnabled)
  const faceOffset = framingEnabled ? autoFaceOffset : manualFaceOffset
  const setFaceOffset = setManualFaceOffset
  // The LOBBY background, distinct from the one in force inside the meeting.
  // It is copied into the meeting at join (VcApp), so what you pick here is what
  // you join wearing — but a change made mid-meeting never writes back here, and
  // neither value is persisted, so a refresh or a return to the dashboard
  // starts clean.
  const selectedBackground = useMeetingPreferencesStore((s) => s.lobbyBackground)
  const setSelectedBackground = useMeetingPreferencesStore((s) => s.setLobbyBackground)
  const [backgroundDialogOpen, setBackgroundDialogOpen] = useState(false)
  const [speakerTestRunning, setSpeakerTestRunning] = useState(false)
  // Which device the blocked-permission dialog is talking about. `null` closes
  // it — one dialog rather than two, because only ever one of them is being
  // explained at a time.
  const [blockedDevice, setBlockedDevice] = useState<'audio' | 'video' | null>(null)
  // Start closed and only open the "ready to join / grant permission" prompt
  // when the user has NOT already granted camera + mic. If both are granted we
  // skip the prompt entirely and go straight to the prejoin preview.
  const [preJoinDialogOpen, setPreJoinDialogOpen] = useState(false)

  // Arriving here is always a fresh start.
  //
  // Cleared on ENTRY rather than by enumerating the ways out. The exits were
  // covered one at a time in VcApp — leave, go home, unmount — and cancelling
  // out of the waiting room was not one of them, so picking a background,
  // being held for a locked meeting and pressing Cancel put you back on this
  // screen still wearing it. Switching to the webinar view and back did the
  // same. Clearing where the page begins covers every route in, including the
  // ones that do not exist yet.
  //
  // The meeting keeps its own copy from the moment you join (VcApp's
  // applyLobbyBackgroundToMeeting), so this cannot disturb a call in progress —
  // this screen is not mounted during one.
  useEffect(() => {
    const prefs = useMeetingPreferencesStore.getState()
    prefs.clearBackgrounds()
    // The camera starts MIRRORED, every time you arrive here: your right hand
    // shows on the right, as in a mirror (BUG-000505 — the true image, right
    // hand on the left, read as flipped). Flip Camera switches to the true
    // image for this visit, not for the next one. The room always sees the
    // true image; this is only how you see yourself.
    //
    // Reset on arrival rather than only on a page load, because reaching this
    // screen from the dashboard is a soft navigation — the store survives it,
    // and a flip from the last meeting would still be on. Resetting on MOUNT
    // also leaves a flip chosen here intact when you join: this runs before the
    // choice, never after it.
    prefs.setMirrorOwnVideo(true)
  }, [])

  useEffect(() => {
    // A passive attendee is never asked. The dialog exists to get a camera and
    // microphone grant before the meeting starts; they publish neither, so it
    // would be asking permission for hardware this meeting will never open —
    // and its buttons trigger the browser prompt directly.
    if (isPassiveJoinUrl()) return

    let cancelled = false
    queryMediaPermissionsGranted().then((granted) => {
      if (!cancelled && !granted) setPreJoinDialogOpen(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // The unblock instructions are opened by an ACTION, not by the preview
  // failing.
  //
  // They used to pop the moment the lobby's own capture was refused, which is
  // the same moment the "ready to join" dialog above opens for exactly the same
  // reason — two stacked dialogs explaining one refusal, the top one hiding the
  // buttons that re-ask. Every route that can be refused now opens them itself:
  // the two toggles, and that dialog's own "use microphone and camera".

  /**
   * Turning a device on in the lobby, which is a request and not a flip.
   *
   * Turning one OFF is always allowed, so it happens immediately. Turning one
   * on asks the browser first and only then moves the control: the toggles used
   * to write `micEnabled`/`camEnabled` unconditionally, so someone who had
   * dismissed the permission prompt could light the camera button over a dead
   * preview — and carry that lie into the meeting, which reads the same flags.
   *
   * A refusal opens the unblock instructions rather than failing quietly: the
   * browser will not prompt a second time once it is remembered, so there is
   * nothing the user can do from this page without them.
   */
  const requestDevice = useCallback(
    async (kind: 'audio' | 'video', enabled: boolean) => {
      const setEnabled = kind === 'audio' ? setMicEnabled : setCamEnabled
      if (enabled) {
        setEnabled(false)
        return
      }
      const outcome = await ensureDevicePermission(kind)
      if (outcome === 'granted') setEnabled(true)
      // Another browser or app has the device. That has been said already, and
      // the unblock instructions would send them to a setting that is fine.
      else if (outcome !== 'in-use') setBlockedDevice(kind)
    },
    [setMicEnabled, setCamEnabled],
  )

  const handleTestSpeaker = useCallback(async () => {
    if (speakerTestRunning) return
    setSpeakerTestRunning(true)
    try {
      await speakerTestService.play(selectedSpeakerId || undefined)
    } finally {
      window.setTimeout(() => setSpeakerTestRunning(false), 500)
    }
  }, [speakerTestRunning, selectedSpeakerId])

  const handleJoinFromLobby = () => {
    const name = flow.userName.trim()
    if (!name || !flow.pendingCode) return
    onJoinMeeting(name, flow.pendingCode)
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

  return (
    <div className="h-[100dvh] bg-pannelcolor text-slate-900 dark:text-foreground">
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div
            className="absolute -left-1/4 -top-1/4 h-1/2 w-1/2 animate-pulse rounded-full bg-indigo-500/10"
            style={{ animationDuration: '8s' }}
          />
          <div
            className="absolute -right-1/4 -bottom-1/4 h-1/2 w-1/2 animate-pulse rounded-full bg-violet-500/10"
            style={{ animationDuration: '10s', animationDelay: '1s' }}
          />
        </div>
      )}

      <div className="relative h-full flex flex-col overflow-hidden">
        <div className="flex-shrink-0 sticky top-0 z-50 bg-pannelcolor">
          <PreJoinHeader
            isAnimating={flow.isAnimating}
            onWebinarClick={onWebinarClick}
            onWaitingLobbyClick={onWaitingLobbyClick}
          />
        </div>

        <main className="flex w-full flex-1 overflow-y-auto px-3 py-4 sm:px-4 sm:py-6 md:py-8 lg:py-10">
          <div className="min-w-0 flex-1 md:flex md:justify-center transition-all duration-300 ease-[cubic-bezier(0.2,0,0,1)]">
            <div
              key="lobby"
              className="px-2 sm:px-4 items-center md:pl-6 lg:pl-10 animate-stepEnter grid w-full grid-cols-1 gap-2 sm:gap-6 lg:gap-8 xl:gap-10 wide-landscape:grid-cols-[1.25fr_0.75fr]"
            >
              <section className="flex flex-col lg:justify-center">
                <DevicePreview
                  stream={videoStream}
                  micLevel={micLevel}
                  camEnabled={viewOnly ? false : camEnabled}
                  micEnabled={viewOnly ? false : micEnabled}
                  viewOnly={viewOnly}
                  permissionDenied={error?.kind === 'permission-denied'}
                  videoFlipped={videoFlipped}
                  framingEnabled={framingEnabled}
                  faceOffset={faceOffset}
                  selectedBackground={selectedBackground}
                  onToggleMic={() => void requestDevice('audio', micEnabled)}
                  onToggleCam={() => void requestDevice('video', camEnabled)}
                  onToggleFlip={() => setMirrorOwnVideo(!videoFlipped)}
                  onSetFramingEnabled={setFramingEnabled}
                  onSetFaceOffset={setFaceOffset}
                  onOpenBackgroundDialog={() => {
                    // Same answer the meeting gives: with no camera there is
                    // nothing to preview an effect against, so say so rather
                    // than opening a dialog that can only show a placeholder.
                    // A passive attendee never publishes video at all, so the
                    // button is equally useless to them.
                    if (viewOnly || !camEnabled) {
                      toast.warning({ title: t('controlBar.cameraOffForBackgrounds') })
                      return
                    }
                    setBackgroundDialogOpen(true)
                  }}
                />

                <DeviceSelectors
                  mics={mics}
                  speakers={speakers}
                  cams={cams}
                  speakerTestRunning={speakerTestRunning}
                  onTestSpeaker={handleTestSpeaker}
                />
              </section>

              <section className="flex justify-center items-center rounded-xl">
                <div className="w-full max-w-md text-slate-900 dark:text-foreground relative">
                  <GettingReadyPanel showReadyToJoin={flow.showReadyToJoin} />
                  <ReadyToJoinPanel
                    showReadyToJoin={flow.showReadyToJoin}
                    userName={flow.userName}
                    pendingCode={flow.pendingCode}
                    onUserNameChange={flow.setUserName}
                    onJoin={handleJoinFromLobby}
                    isWebinar={isWebinar}
                    joinBlocked={joinBlocked}
                  />
                </div>
              </section>
            </div>
          </div>
        </main>

        <footer className="w-full sticky bottom-0 z-50 py-3 sm:py-4 px-3 sm:px-4 text-center text-[10px] sm:text-xs text-slate-500 dark:text-muted-foreground flex-shrink-0">
          <p className="leading-relaxed">
            {t('dashboard.byUsingInMeet')}{' '}
            <LegalLink href={config.legal.termsUrl}>
              {t('dashboard.termsOfService')}
            </LegalLink>
            ,{' '}
            <LegalLink href={config.legal.privacyUrl}>
              {t('dashboard.privacyStatement')}
            </LegalLink>
            ,{' '}
            <LegalLink href={config.legal.noticesUrl}>
              {t('dashboard.noticesDisclaimers')}
            </LegalLink>
            .{' '}
            <LegalLink href={config.legal.learnMoreUrl}>
              {t('dashboard.learnMoreAboutInMeet')}
            </LegalLink>
            .
          </p>
        </footer>

        <BackgroundEffectsDialog
          open={backgroundDialogOpen}
          selectedBackgroundId={selectedBackground?.id ?? null}
          selectedBackground={selectedBackground}
          previewStream={videoStream}
          onClose={() => setBackgroundDialogOpen(false)}
          onSelectBackground={(bg) => {
            // Commit happens via the dialog's Apply button; this callback
            // pushes the chosen background into the lobby preview.
            setSelectedBackground(bg)
            setBackgroundDialogOpen(false)
          }}
          onClearBackground={() => {
            setSelectedBackground(null)
            setBackgroundDialogOpen(false)
          }}
        />

        <MicPermissionDialog
          open={blockedDevice !== null}
          onOpenChange={(open) => {
            if (!open) setBlockedDevice(null)
          }}
          kind={blockedDevice ?? 'audio'}
          // "Try again" goes back through the gate rather than writing the flag
          // directly. If the block is still in place the control stays off and
          // the dialog comes back, which is the honest outcome — the old
          // version set `micEnabled = true` unconditionally and left the button
          // lit over a microphone the browser was still refusing.
          onTryAgain={() => {
            const kind = blockedDevice ?? 'audio'
            setBlockedDevice(null)
            window.setTimeout(() => void requestDevice(kind, false), 500)
          }}
        />

        <DevicePermissionDialog
          open={preJoinDialogOpen}
          onOpenChange={setPreJoinDialogOpen}
          // What the user ASKED for, not what they got. Each kind goes through
          // the gate, so "Use microphone and camera" leaves the camera off if
          // the camera is the one that was refused.
          onJoin={({ audio, video }) => {
            setPreJoinDialogOpen(false)
            if (audio) void requestDevice('audio', false)
            else setMicEnabled(false)
            if (video) void requestDevice('video', false)
            else setCamEnabled(false)
          }}
          userName={flow.userName}
        />
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardEnter {
          from { opacity: 0; transform: translateY(14px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideDown { animation: slideDown 0.4s ease-out; }
        @keyframes stepEnter {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-stepEnter { animation: stepEnter 260ms cubic-bezier(0.2,0,0,1); }
      `}</style>
    </div>
  )
}
